<script setup>
import { computed } from 'vue';
import { shortDate } from '../../helpers/usDate';
import FormDropdown from '../../pge-components/pge-dropdown/pge-dropdown.vue';

const props = defineProps({
  modelValue: { type: [Number, null], default: null },
  brokers: { type: Array, default: () => [] },
  // The broker on record before this modal was opened, so the consequence of a
  // change can be stated before it is saved.
  originalBrokerId: { type: [Number, null], default: null },
  personName: { type: String, default: 'This person' },
  canAssign: { type: Boolean, default: false },
  // Append-only entries from /users/{id}/broker-history, newest first. Shown to
  // owner and hub staff only — a broker has no business reading who held this
  // customer before them.
  history: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);

const NOBODY = '';

const nameOf = broker =>
  broker ? `${broker.first_name ?? ''} ${broker.last_name ?? ''}`.trim() || broker.email : '';

const firstNameOf = broker => nameOf(broker).split(' ')[0] || '';

const brokerById = id => props.brokers.find(b => b.id === id) || null;

const selected = computed(() => brokerById(props.modelValue));
const original = computed(() => brokerById(props.originalBrokerId));

const options = computed(() => [
  { label: '— Nobody yet', value: NOBODY },
  ...props.brokers.map(broker => ({
    label: `${nameOf(broker)} · ${broker.book_size} ${broker.book_size === 1 ? 'policyholder' : 'policyholders'}`,
    value: broker.id,
  })),
]);

const dropdownValue = computed({
  get: () => (props.modelValue === null ? NOBODY : props.modelValue),
  set: value => emit('update:modelValue', value === NOBODY || value === null ? null : Number(value)),
});

const personFirstName = computed(() => props.personName.split(' ')[0] || props.personName);

const isMoving = computed(
  () => props.originalBrokerId !== null && props.modelValue !== null && props.modelValue !== props.originalBrokerId
);

const isRemoving = computed(() => props.originalBrokerId !== null && props.modelValue === null);

const nameOfId = id => {
  const broker = brokerById(id);
  return broker ? nameOf(broker) : 'a broker who has since left';
};

const formatWhen = (value) => shortDate(value);

// A first assignment has no `from`, so describing it as a move from nobody would
// invent a predecessor that never existed.
const historyLines = computed(() =>
  props.history.map(entry => ({
    id: entry.id,
    when: formatWhen(entry.created_at),
    text: entry.from_broker_id === null
      ? `Assigned to ${nameOfId(entry.to_broker_id)}`
      : entry.to_broker_id === null
        ? `Removed from ${nameOfId(entry.from_broker_id)}`
        : `Moved from ${nameOfId(entry.from_broker_id)} to ${nameOfId(entry.to_broker_id)}`,
  }))
);
</script>

<template>
  <div class="broker-card" data-testid="broker-card">
    <span class="broker-card__label">Their broker</span>

    <template v-if="canAssign">
      <FormDropdown
        name="broker_id"
        label-type="separated"
        :model="dropdownValue"
        v-model="dropdownValue"
        :options="options"
        data-testid="broker-card-select"
      />

      <p v-if="selected" class="broker-card__help" data-testid="broker-card-help">
        {{ nameOf(selected) }} currently looks after {{ selected.book_size }}
        {{ selected.book_size === 1 ? 'policyholder' : 'policyholders' }}. They'll see this person in their book,
        and this person will see {{ firstNameOf(selected) }} as their contact in the portal.
      </p>

      <div v-else class="broker-card__note" data-testid="broker-card-unassigned">
        {{ personFirstName }} has nobody to message until a broker is assigned.
      </div>

      <div v-if="isMoving" class="broker-card__warn" data-testid="broker-card-moving">
        {{ personFirstName }} will see this. {{ firstNameOf(selected) }} replaces {{ firstNameOf(original) }}
        as their contact in the portal, and the chat history moves across.
      </div>

      <div v-if="isRemoving" class="broker-card__warn" data-testid="broker-card-removing">
        {{ personFirstName }} will have nobody to message until you assign someone else.
        Their existing conversation is preserved.
      </div>

      <div class="broker-card__log">
        <span class="broker-card__log-label">Assignment history</span>

        <ol v-if="historyLines.length" class="broker-card__history" data-testid="broker-card-history">
          <li v-for="line in historyLines" :key="line.id">
            <span>{{ line.text }}</span>
            <time v-if="line.when">{{ line.when }}</time>
          </li>
        </ol>

        <p v-else class="broker-card__help" data-testid="broker-card-history-empty">
          Nothing yet — this person has not been reassigned.
        </p>
      </div>
    </template>

    <p v-else class="broker-card__readonly" data-testid="broker-card-readonly">
      {{ selected ? nameOf(selected) : 'Nobody yet' }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
.broker-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: var(--xl-font-family);

  &__label {
    font-size: var(--xl-type-label-large-size);
    line-height: var(--xl-type-label-large-line);
    font-weight: var(--xl-font-weight-bold);
    color: var(--xl-color-primary-text);
    cursor: default;
  }

  &__help,
  &__readonly {
    margin: 0;
    font-size: var(--xl-font-size-sm);
    color: var(--xl-color-secondary-text);
  }

  &__note,
  &__warn {
    padding: 12px 15px;
    border-radius: var(--xl-radius-sm);
    font-size: var(--xl-type-body-medium-size);
    line-height: var(--xl-type-body-medium-line);
  }

  // Unassigned is a normal state, so this is the neutral surface, not the error
  // one. Nothing about having no broker yet is broken.
  &__note {
    background: var(--xl-color-surface-muted, #f2f2f4);
    color: var(--xl-color-secondary-text);
  }

  &__log {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 4px;
  }

  &__log-label {
    font-size: var(--xl-type-label-large-size);
    font-weight: var(--xl-font-weight-bold);
    color: var(--xl-color-primary-text);
  }

  &__history {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 4px;

    li {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      font-size: var(--xl-font-size-sm);
      color: var(--xl-color-secondary-text);
      padding: 6px 0;
      border-bottom: 1px solid var(--xl-color-border);
    }

    li:last-child { border-bottom: 0; }

    time { white-space: nowrap; }
  }

  &__warn {
    background: color-mix(in srgb, var(--xl-color-warning) 16%, white);
    color: var(--xl-color-warning);
  }
}
</style>
